/*
 * VerificationResults: how each suite's report is drawn.
 *
 * One renderer per suite id, keyed at the bottom. A suite the console has no
 * renderer for falls back to KeyValueResult, which lists the report's own
 * fields, so a suite that appears on an appliance shows its result before
 * the console learns to draw it. The verdict, the reason and the provenance
 * are drawn by the panel for every suite alike; a renderer draws only the
 * measurement.
 *
 * ! file components/VerificationResults.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import React, { useState } from "react";
import { FiCheckCircle, FiXCircle, FiAlertCircle, FiInfo, FiSlash, FiMinusCircle } from "react-icons/fi";
import { fmtDuration, fmtBitRate } from "../utils/formatters";

/* Status is reserved for status. These three never appear as a series colour,
   and each carries an icon and a word, so a verdict never rests on hue alone. */
const VERDICT = {
  pass: { label: "PASS", cls: "is-pass", Icon: FiCheckCircle },
  fail: { label: "FAIL", cls: "is-fail", Icon: FiXCircle },
  inconclusive: { label: "INCONCLUSIVE", cls: "is-inconclusive", Icon: FiAlertCircle },
};

export function verdictClass(verdict) {
  return (VERDICT[verdict] || VERDICT.inconclusive).cls;
}

/* label is what the run found, for a suite whose verdict alone cannot say it: one
   that never passes reads INCONCLUSIVE both when it found nothing and when it
   could not look. */
export function VerdictBadge({ verdict, label }) {
  // An unknown verdict from a newer appliance is shown as itself rather than
  // quietly relabelled as one of ours.
  const v = VERDICT[verdict];
  const { label: word, cls, Icon } = v || { ...VERDICT.inconclusive, label: String(verdict || "unknown").toUpperCase() };
  return (
    <span className={`vfy-badge ${cls}`}>
      <Icon size={12} aria-hidden="true" />
      {word}
      {label && (
        <>
          <span aria-hidden="true">·</span>
          <span style={{ fontWeight: 600, letterSpacing: 0 }}>{label}</span>
        </>
      )}
    </span>
  );
}

export const muted = { fontSize: "0.78rem", color: "#64748b" };
export const th = { padding: "4px 10px 4px 0", fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em" };
export const td = { padding: "6px 10px 6px 0", color: "#0f172a" };

const fmtMbps = (v) => fmtBitRate(v);
const fmtRatio = (r) => (r > 0 ? `${r.toFixed(2)}×` : "—");

/* Fields every report carries; the panel draws them, so the fallback skips them. */
const META_KEYS = new Set(["id", "started_at", "finished_at", "verdict", "reason", "cancelled", "error",
  "ended", "provenance", "params", "claim", "rule", "limits", "plain", "suite", "seq", "prev", "signer", "finding"]);

/* The words a run was actually graded against.
 *
 * A descriptor is computed when the console asks for it, so the rule printed
 * beside an old verdict is whatever the appliance grades by now. A report made
 * since those words started being recorded carries its own; one made before
 * does not. Which of the two a reader is looking at is stated, because the
 * silent substitution is the thing that makes a forwarded verdict worthless. */
export function resolveWords(desc, report) {
  const recorded = !!(report && (report.rule || report.claim || report.limits?.length));
  return {
    claim: recorded ? report.claim : desc?.claim,
    rule: recorded ? report.rule : desc?.rule,
    limits: (recorded ? report.limits : desc?.limits) || [],
    recorded,
    // Both exist and disagree: the appliance has been updated since the run.
    drifted: recorded && !!desc?.rule && !!report.rule && desc.rule !== report.rule,
  };
}

export const RULE_SOURCE = {
  recorded: "as recorded with this run",
  live: "read from this appliance now, not stored with this run",
};

/* Per-suite measurement, as text. Keyed like RESULTS, for the same reason: a
   suite the console has no renderer for still produces a forwardable result. */
const TEXT = {
  bond: (report, wanName) => {
    const rows = (report.directions || []).map((d) => {
      const alone = Object.entries(d.alone_median_mbps || {})
        .map(([n, v]) => `${wanName(n)} alone ${fmtMbps(v)}`).join(", ");
      return `  ${d.direction}: bonded ${fmtMbps(d.bonded_median_mbps || 0)} against ${alone}`
        + ` (${fmtRatio(d.ratio)}) ${String(d.verdict || "").toUpperCase()}`;
    });
    const counts = (report.directions || []).flatMap((d) => {
      const arms = [["bonded", d.bonded], ...Object.entries(d.alone || {})];
      const parts = arms.map(([name, samples]) => (name === "bonded"
        ? describeArm("bonded", samples, true)
        : describeArm(`${wanName(name)} alone`, samples, false)));
      return parts.length ? [`  ${d.direction}  ${parts.join(" · ")}`] : [];
    });
    return [...rows, "", ...counts];
  },
  soak: (report, wanName) => {
    const counts = report.breach_counts || {};
    const tally = Object.entries((report.windows || []).reduce((acc, w) => ({ ...acc, [w.status]: (acc[w.status] || 0) + 1 }), {}))
      .map(([status, n]) => `${n} ${status}`).join(", ");
    const out = [
      `  measured ${fmtLength(report.measured_sec)}, excluded ${fmtLength(report.excluded_sec)}, worst silence graded `
        + `${report.worst_gap_ms > 0 ? fmtSpan(report.worst_gap_ms) : "none"}, striped ${fmtLength(report.striped_sec)}`
        + ` (striping gate open ${fmtLength(report.gate_open_sec)})`,
      `  breaks: ${counts.failing || 0} failing, ${counts.documented || 0} documented recovery, `
        + `${counts.below_detection || 0} below detection`,
      ...(report.breaches || []).map((b) => `  ${b.at}  ${breachWhat(b)}  ${BREACH[b.class]?.word || b.class}`
        + `  ${breachCircuits(b, wanName)}  ${b.explanation}`),
      `  windows: ${tally || "none"}`,
    ];
    const lines = report.direct_lines || {};
    if (lines.reason) out.push(`  ${lines.enabled ? "direct lines" : "below detection off"}: ${lines.reason}`);
    if (report.cuts?.length) {
      out.push(`  cuts every ${report.params?.cut_every_min} min: ${report.cuts.length}`);
      for (const c of report.cuts) out.push(`  cut ${c.n} at ${c.at} on ${wanName(c.circuit)}${c.striped ? " (striped)" : ""}: ${cutOutcome(c)}`);
    }
    return out;
  },
  diversity: (report, wanName) => {
    const out = [`  up to the cloud anchor ${report.anchor?.host || "unknown"}, which every circuit shares`];
    for (const p of report.pairs || []) {
      out.push("", `  ${wanName(p.a)} and ${wanName(p.b)}: ${p.finding}${p.nearest ? `, nearest ${p.nearest}` : ""}`);
      for (const [name, row] of DIVERSITY_ROWS.map(([key, title]) => [title, p[key]])) {
        out.push(`    ${name}: ${row?.finding || "not measured"}, ${row?.reason || ""}`);
      }
      for (const d of p.capacity?.directions || []) {
        const idx = (d.blocks || []).map((b) => (b.void_reason ? "void" : fmtIndex(b.index))).join(", ");
        out.push(`      ${d.direction} index per block: ${idx}`);
      }
    }
    return out;
  },
  failover: (report, wanName) => {
    const out = (report.events || []).map((e) => {
      const call = e.on_target ? `silence ${e.gap_sec} s, loss ${fmtPct(e.loss_pct)}` : "not carrying the call, not scored";
      const held = sessionsAcross(e);
      return `  failure ${e.n} on ${e.wan ? wanName(e.wan) : "no circuit"}: ${call}${held ? `; held sessions: ${held}` : ""}`;
    });
    if (report.session_peer) {
      out.push("", `  ${farEnd(report, wanName)}`);
      for (const s of report.sessions || []) {
        out.push(`  session ${s.n}, port ${s.local_port}, opened on ${wanName(s.circuit_at_open)}: sent ${s.sent}, `
          + `received ${s.received}, far end received ${s.peer_received}, gaps ${s.gaps}, duplicates ${s.duplicates}`
          + (s.error ? `, ended: ${s.error}` : ""));
      }
    }
    if (report.continuity_reason) out.push("", `  ${report.continuity_reason}`);
    return out;
  },
  watch: (report, wanName) => {
    const out = [`  watched ${fmtLength(report.watched_sec || 0)}, ${(report.events || []).length} failures seen, `
      + `${report.events_scored || 0} under the call, longest break ${report.worst_gap_sec || 0} s against ${report.bound_sec} s`];
    for (const e of report.events || []) {
      const f = failureWords(e, wanName);
      out.push(`  ${e.at || `failure ${e.n}`}  ${[f.what, f.call, f.sessions, f.address, f.calls, e.note].filter(Boolean).join(" ")}`);
    }
    if (report.session_peer) {
      out.push("", `  ${farEnd(report, wanName)}`);
      for (const s of report.sessions || []) {
        out.push(`  session ${s.n}, port ${s.local_port}, opened on ${wanName(s.circuit_at_open)}: sent ${s.sent}, `
          + `received ${s.received}, far end received ${s.peer_received}, gaps ${s.gaps}, duplicates ${s.duplicates}`
          + (s.error ? `, ended: ${s.error}` : ""));
      }
    }
    if (report.breaks_total) {
      out.push("", `  silences no failure explains: ${report.breaks_total}`,
        ...(report.breaks || []).map((b) => `  ${b.at}  ${b.gap_sec} s`));
    }
    if (report.continuity_reason) out.push("", `  ${report.continuity_reason}`);
    return out;
  },
};

/* n, the range, and how many streams actually completed. A median over three
   samples where three of sixteen streams completed is not the same evidence as
   a median over three clean ones, and only this line separates them. */
function describeArm(label, samples, bonded) {
  const got = (samples || []).filter((x) => x && x.mbps > 0);
  // A block that did not measure its condition was left out of the median: a
  // bonded one measured one link, because its flows did not spread across the
  // ring or the ring held one link; an alone one measured the other links,
  // because the held link went silent and left the hold. Saying n=3 here while
  // the verdict used one would be the console inflating the evidence.
  const ok = got.filter((x) => !x.off_ring);
  const off = got.length - ok.length;
  // A stream the classifier put in a class that does not bond ignored the ring,
  // whichever condition its block was.
  const unbonded = got.find((x) => x.off_ring && x.not_bonded)?.not_bonded;
  const why = unbonded ? `its streams landed in ${unbonded}, which does not bond`
    : bonded ? "measured one link, not the bond" : "the link left the ring while held";
  const aside = off > 0 ? `, ${off} block${off === 1 ? "" : "s"} left out: ${why}` : "";
  if (ok.length === 0) {
    return off > 0 ? `${label} no usable samples${aside}` : `${label} no samples`;
  }
  const lo = Math.min(...ok.map((x) => x.mbps));
  const hi = Math.max(...ok.map((x) => x.mbps));
  const streamsOK = ok.reduce((n, x) => n + (x.streams_ok || 0), 0);
  const streamsAll = streamsOK + ok.reduce((n, x) => n + (x.streams_failed || 0), 0);
  const range = lo === hi ? fmtMbps(lo) : `${fmtMbps(lo)} to ${fmtMbps(hi)}`;
  return `${label} n=${ok.length}, ${range}, ${streamsOK} of ${streamsAll} streams completed${aside}`;
}

/* Configured against measured. A run that covered only half of what was asked
   for renders today as a clean verdict with nothing saying so. */
export function coverageOf(report) {
  const want = report?.params?.directions;
  const got = (report?.directions || []).map((d) => d.direction);
  if (!Array.isArray(want) || want.length === 0) return null;
  const missing = want.filter((d) => !got.includes(d));
  return missing.length === 0
    ? { complete: true, text: "Every configured condition produced a result." }
    : { complete: false, text: `Configured but not measured: ${missing.join(", ")}.` };
}

/* The run as plain text, for forwarding. Every value comes from the same
   resolver the card uses, so a paste and a screenshot cannot disagree. */
export function reportText({ desc, report, wanName = (n) => n }) {
  if (!report) return "";
  const w = resolveWords(desc, report);
  const p = report.provenance || {};
  const cov = coverageOf(report);
  const out = [
    `run ${report.id || "?"} · node ${p.node_id || "unknown"}`,
    (desc?.title || "Verification run").toUpperCase(),
  ];
  if (w.claim) out.push(w.claim);
  const verdict = String(report.verdict || "?").toUpperCase() + (report.finding ? `: ${report.finding}` : "");
  out.push("", `[ ${verdict} ]  ${(report.reason || "").trim()}`);
  if (report.next) out.push("", "WHAT TO DO", `  ${report.next}`);
  out.push(`run ${report.id || "?"} on node ${p.node_id || "unknown"} · ${report.started_at} to ${report.finished_at} UTC · ended ${report.ended || "?"}`);
  out.push("", `GRADED AGAINST THIS RULE  (${w.recorded ? RULE_SOURCE.recorded : RULE_SOURCE.live})`, `  ${(w.rule || "not stated").trim()}`);
  if (w.drifted) out.push("  This appliance now grades by a different rule than the one above.");

  const measurement = TEXT[desc?.id]?.(report, wanName);
  if (measurement?.length) out.push("", "THE MEASUREMENT", ...measurement);
  if (cov) out.push(`  ${cov.text}`);
  if (report.carrier_changed?.length) {
    out.push(`  Link event on ${report.carrier_changed.map(wanName).join(", ")} during the run; the run is discarded.`);
  }

  if (w.limits.length) out.push("", "WHAT THIS CANNOT SEE", ...w.limits.map((l) => `  · ${l}`));

  const params = Object.entries(report.params || {}).map(([k, v]) => `${k} ${Array.isArray(v) ? v.join(", ") : v}`);
  if (params.length) out.push("", "RUN PARAMETERS", `  ${params.join(" · ")}`);

  out.push("", "APPLIANCE",
    `  node ${p.node_id || "unknown"} · build ${p.build?.revision ? p.build.revision.slice(0, 10) + (p.build.modified ? " (modified)" : "") : "not stamped"}`
    + ` · anchor ${p.anchor_host || "unknown"} · WANs ${(p.wans || []).map((x) => wanName(x.name)).join(" + ") || "none"}`
    + ` · impairments ${p.impairments_active?.length ? p.impairments_active.map(wanName).join(", ") : "none"}`
    + ` · classifier ${p.classifier_mode || "unknown"}`);
  return out.join("\n");
}

export function KeyValueResult({ report }) {
  const rows = Object.entries(report || {}).filter(([k]) => !META_KEYS.has(k));
  if (rows.length === 0) return null;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: "0.78rem" }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderTop: "1px solid #f1f5f9", verticalAlign: "top" }}>
              <td style={{ ...td, color: "#64748b", whiteSpace: "nowrap" }}>{k}</td>
              <td style={td}>
                {v !== null && typeof v === "object"
                  ? <pre style={{ margin: 0, fontSize: "0.72rem", whiteSpace: "pre-wrap" }}>{JSON.stringify(v, null, 1)}</pre>
                  : String(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* The bond do-no-harm run: per direction, the bond against each link alone.
 *
 * Bars rather than a table because the question is a magnitude comparison and
 * the answer should survive a glance. The bonded bar carries the accent and the
 * alone bars recede, since they are the baseline it is read against; the best of
 * them is named in the row rather than recoloured, so nothing here depends on
 * telling two hues apart. Every bar is labelled with its own value. */
function BondResult({ report, wanName }) {
  const dirs = report.directions || [];
  if (dirs.length === 0) return null;

  return (
    <div>
      {dirs.map((d) => {
        const alone = d.alone_median_mbps || {};
        const bonded = d.bonded_median_mbps || 0;
        const bestMbps = d.best_alone_mbps || alone[d.best_alone] || 0;
        const peak = Math.max(bonded, ...Object.values(alone), 1);
        const rows = [
          { key: "__bonded", name: "Bonded", mbps: bonded, bonded: true, samples: d.bonded },
          ...Object.entries(alone).map(([name, mbps]) => ({
            key: name, name: `${wanName(name)} alone`, mbps, best: name === d.best_alone,
            samples: (d.alone || {})[name],
          })),
        ];
        const split = [...(d.bonded || [])].reverse().find((x) => x.split && Object.keys(x.split).length)?.split;
        const over = Object.entries(d.oversubscription || {}).filter(([, v]) => v > 1.3);

        return (
          <div className="vfy-dir" key={d.direction}>
            <div className="vfy-dir-head">
              <span>{d.direction}</span>
              {dirs.length > 1 && <VerdictBadge verdict={d.verdict} />}
            </div>

            <div className="vfy-kpis">
              <div className="stat-card">
                <div className="stat-label">Bonded</div>
                <div className="stat-value">{fmtMbps(bonded)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Best link alone</div>
                <div className="stat-value">{fmtMbps(bestMbps)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Ratio</div>
                <div className="stat-value">{fmtRatio(d.ratio)}</div>
              </div>
            </div>

            {rows.map((r) => (
              <div className="vfy-bar-row" key={r.key}>
                <span className="vfy-bar-name">
                  {r.name}{r.best && <> <span className="vfy-bar-best">best</span></>}
                </span>
                <span className="vfy-bar-track">
                  <span className={`vfy-bar-fill${r.bonded ? "" : " vfy-bar-fill--alone"}`}
                    style={{ width: `${Math.max(2, (100 * r.mbps) / peak)}%` }} />
                </span>
                <span className="vfy-bar-value">{fmtMbps(r.mbps)}</span>
              </div>
            ))}

            <div className="vfy-samples">
              {rows.map((r) => describeArm(r.name, r.samples, r.bonded)).join(" · ")}
            </div>

            {split && (
              <div className="vfy-samples">
                {d.direction}: bonded bytes went {Object.entries(split)
                  .map(([n, f]) => `${wanName(n)} ${Math.round(f * 100)}%`).join(", ")}.
              </div>
            )}
            {over.map(([n, v]) => (
              <div className="vfy-samples" key={n}>
                The allocator placed {wanName(n)} at {v.toFixed(1)}× what it can carry at the best link's pace.
              </div>
            ))}
          </div>
        );
      })}

      {report.carrier_changed?.length > 0 && (
        <div className="vfy-drift">
          Link event on {report.carrier_changed.map(wanName).join(", ")} during the run; the run is discarded.
        </div>
      )}
    </div>
  );
}

/* Encryption cost, and whether the post-quantum key exchange really ran. */
function LaneResult({ report }) {
  const arms = report.arms || [];
  const off = arms.find((a) => a.suite === "off");
  return (
    <div>
      {report.decision && <div className="vfy-plain" style={{ fontWeight: 600 }}>{report.decision}</div>}
      <div className="vfy-kpis">
        <div className="stat-card">
          <div className="stat-label">Leaving the kernel path</div>
          <div className="stat-value">{fmtAdded(report.lane_cost_ms, report.off_spread_ms)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Post-quantum on top</div>
          <div className="stat-value">{fmtAdded(report.pq_over_strong_ms, report.off_spread_ms)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Bound</div>
          <div className="stat-value">{report.bound_ms} ms</div>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 520 }}>
          <thead>
            <tr style={{ color: "#64748b", textAlign: "left" }}>
              <th style={th}>Suite</th><th style={th}>Median</th><th style={th}>Added</th>
              <th style={th}>Jitter p95</th><th style={th}>Loss</th><th style={th}>Key exchange</th><th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {arms.map((a) => (
              <tr key={a.suite} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={{ ...td, fontWeight: 700 }}>{a.suite}</td>
                <td style={td}>{a.median_ms} ms</td>
                <td style={td}>{a.suite === "off" ? "baseline" : fmtAdded(a.added_median_ms, report.off_spread_ms)}</td>
                <td style={td}>{a.jitter_p95_ms} ms</td>
                <td style={td}>{fmtPct(a.loss_pct)}</td>
                <td style={td}>
                  {a.handshake?.expect_kem_pub_bytes
                    ? `${a.handshake.kem_pub_bytes} / ${a.handshake.kem_ct_bytes} B`
                    : "classical"}
                </td>
                <td style={td}>{a.suite !== "off" && <VerdictBadge verdict={a.verdict} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {arms.filter((a) => a.handshake?.note).map((a) => (
        <div className="vfy-samples" key={a.suite}>{a.suite}: {a.handshake.note}</div>
      ))}
      {arms.filter((a) => a.off_lane > 0).map((a) => (
        <div className="vfy-drift" key={`${a.suite}-off-lane`}>
          {a.suite}: {a.off_lane} {a.off_lane === 1 ? "block" : "blocks"} left out, because {a.off_lane_reason}.
        </div>
      ))}
      {off && <div className="vfy-samples">Unencrypted baseline {off.median_ms} ms, and it moved
        {" "}{report.off_spread_ms} ms between its own blocks: differences smaller than that are not measurements.</div>}
    </div>
  );
}

/* What a call would experience. Each figure against the bound it is graded on. */
function VoiceResult({ report }) {
  // Why each bound is what it is lives in the suite's rule, which the panel
  // draws beside the verdict. Restating it here would put the same reasoning
  // on screen twice and let the two copies drift apart.
  const rows = [
    { label: "Delay variation", value: report.jitter_p95_ms, unit: "ms", bound: report.jitter_bound_ms },
    { label: "Loss", value: report.loss_pct, unit: "%", bound: report.loss_bound_pct },
    { label: "Longest silence", value: report.worst_gap_ms, unit: "ms", bound: report.gap_bound_ms },
  ];
  return (
    <div>
      <div className="vfy-kpis">
        {rows.map((r) => (
          <div className="stat-card" key={r.label}>
            <div className="stat-label">{r.label}</div>
            <div className="stat-value" style={{ color: r.value > r.bound ? "#991b1b" : undefined }}>
              {r.value}{r.unit === "%" ? "%" : ` ${r.unit}`}
            </div>
            <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>bound {r.bound}{r.unit === "%" ? "%" : ` ${r.unit}`}</div>
          </div>
        ))}
      </div>
      <div className="vfy-samples">
        Round trip {report.median_ms} ms median, {report.p95_ms} ms at p95, over {(report.blocks || []).length} calls.
      </div>
    </div>
  );
}

/* Our path against the bare circuit, per circuit. */
function TunnelResult({ report, wanName }) {
  const cs = report.circuits || [];
  return (
    <div>
      {report.peer_is_anchor && (
        <div className="vfy-samples" style={{ color: "#92400e" }}>
          No far end was named, so both arms end at our own anchor and its hop cancels. These numbers are the
          cost of the encapsulation itself and carry no verdict.
        </div>
      )}
      {cs.map((c) => {
        const peak = Math.max(c.direct?.median_ms || 0, c.anchored?.median_ms || 0, 1);
        const rows = [
          { key: "direct", name: `${wanName(c.wan)} direct`, ms: c.direct?.median_ms || 0 },
          { key: "anchored", name: `${wanName(c.wan)} through the tunnel`, ms: c.anchored?.median_ms || 0, tunnel: true },
        ];
        return (
          <div className="vfy-dir" key={c.wan}>
            <div className="vfy-dir-head">
              <span>{wanName(c.wan)}</span>
              <VerdictBadge verdict={c.healthy_verdict} />
            </div>
            {rows.map((r) => (
              <div className="vfy-bar-row" key={r.key}>
                <span className="vfy-bar-name">{r.name}</span>
                <span className="vfy-bar-track">
                  <span className={`vfy-bar-fill${r.tunnel ? "" : " vfy-bar-fill--alone"}`}
                    style={{ width: `${Math.max(2, (100 * r.ms) / peak)}%` }} />
                </span>
                <span className="vfy-bar-value">{r.ms} ms</span>
              </div>
            ))}
            <div className="vfy-samples">
              The tunnel added {c.added_median_ms > (c.direct_spread_ms || 0) ? `${c.added_median_ms} ms` : "no delay this run could measure"}
              {" "}and {c.added_jitter_ms > 0 ? `${c.added_jitter_ms} ms of jitter` : "no jitter"}.
              {c.direct?.loss_floor_pct ? ` Loss below ${c.direct.loss_floor_pct}% is below this run's resolution and is not graded.` : ""}
            </div>
            {c.impaired_anchored && (
              <div className="vfy-samples">
                Impaired: the tunnel delivered {fmtPct(c.impaired_anchored.loss_pct)} loss at {c.impaired_anchored.median_ms} ms
                against the bare circuit's {fmtPct(c.impaired_direct.loss_pct)} at {c.impaired_direct.median_ms} ms.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* What a circuit failure costs a live session. */
function FailoverResult({ report, wanName }) {
  const evs = report.events || [];
  // A run given a session far end held order-style sessions through every cut.
  // One that predates the arm, or was run without a far end, has no session
  // fields at all, and says so rather than showing zeroes.
  const held = !!report.session_peer;
  const sessions = report.sessions || [];
  return (
    <div>
      <div className="vfy-kpis">
        <div className="stat-card">
          <div className="stat-label">Longest silence</div>
          <div className="stat-value">{report.worst_gap_sec} s</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Bound</div>
          <div className="stat-value">{report.bound_sec} s</div>
          <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>of which up to {report.prober_band_sec} s is prober timing</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Failures scored</div>
          <div className="stat-value">{report.events_scored} of {evs.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Sessions kept</div>
          <div className="stat-value">{held ? `${sessions.filter((s) => s.kept).length} of ${sessions.length}` : "not checked"}</div>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 460 }}>
          <thead>
            <tr style={{ color: "#64748b", textAlign: "left" }}>
              <th style={th}>Failure</th><th style={th}>Circuit</th><th style={th}>Carried the call</th>
              <th style={th}>Silence</th><th style={th}>Loss</th>
              {held && <th style={th}>Sessions</th>}
            </tr>
          </thead>
          <tbody>
            {evs.map((e) => (
              <tr key={e.n} style={{ borderTop: "1px solid #f1f5f9", opacity: e.on_target ? 1 : 0.55 }}>
                <td style={td}>{e.n}</td>
                <td style={td}>{wanName(e.wan)}</td>
                <td style={td}>{e.on_target ? "yes" : "no, not scored"}</td>
                <td style={{ ...td, fontWeight: 700 }}>{e.on_target ? `${e.gap_sec} s` : "—"}</td>
                <td style={td}>{fmtPct(e.loss_pct)}{e.note ? <div style={{ ...muted, fontSize: "0.68rem" }}>{e.note}</div> : null}</td>
                {held && (
                  <td style={{ ...td, opacity: (e.sessions || []).some((s) => s.scored) ? 1 : 0.55 }}>
                    {sessionsAcross(e) || "—"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {held && sessions.length > 0 && (
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 560 }}>
            <thead>
              <tr style={{ color: "#64748b", textAlign: "left" }}>
                <th style={th}>Session port</th><th style={th}>Circuit at open</th><th style={th}>Sent</th>
                <th style={th}>Received</th><th style={th}>Far end received</th><th style={th}>Gaps</th>
                <th style={th}>Duplicates</th><th style={th}>Error</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.n} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={td}>{s.local_port}</td>
                  <td style={td}>{wanName(s.circuit_at_open)}</td>
                  <td style={td}>{s.sent}</td>
                  <td style={td}>{s.received}</td>
                  <td style={td}>{s.peer_received}</td>
                  <td style={td}>{s.gaps}</td>
                  <td style={td}>{s.duplicates}</td>
                  <td style={{ ...td, color: s.error ? "#991b1b" : undefined }}>{s.error || "none"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {held && <div className="vfy-samples">{farEnd(report, wanName)}</div>}

      <div className="vfy-samples">
        {report.continuity_checked
          ? report.continuity_reason
          : "No far end was configured to watch the public address, so this run reports the silence and makes no claim about the session surviving."}
      </div>
    </div>
  );
}

/* One failure a watch saw, as the sentences a person would say about it: what
   failed, what it did to the call, and what carried on. Every figure behind a
   sentence is in the measurements table underneath. */
const FAILED = {
  silent: "stopped carrying traffic with its cable still in",
  link: "lost its link",
};

function failureWords(e, wanName) {
  const circuit = e.wan ? wanName(e.wan) : "A circuit";
  const what = `${circuit} ${FAILED[e.kind] || "failed"}`
    + (e.cause === "impairment" ? ", under an impairment applied on this appliance." : ".");
  let call;
  if (e.open) {
    call = e.gap_sec > 0
      ? `Still measuring. So far the call has been broken for ${e.gap_sec} s.`
      : "Still measuring what it did to the call.";
  } else if (e.no_path) {
    call = "Every circuit was dark at once, so there was nothing to fail onto. Not scored.";
  } else if (!e.on_target) {
    call = "The call was on another circuit and was not touched. Not scored.";
  } else if (e.striped) {
    call = `The call was spread over both circuits, and its audio was broken for ${e.gap_sec} s before it carried on.`;
  } else {
    call = `The call was on it and went silent for ${e.gap_sec} s, then carried on over another circuit.`;
  }
  const held = e.open ? null : sessionsAcross(e);
  const sessions = held && `Order sessions: ${held}.`;
  let address = null;
  if (!e.open && e.address_after) {
    address = e.survived_same_address
      ? `The far end kept seeing ${e.address_after}.`
      : `The far end saw the address change from ${e.address_before || "unknown"} to ${e.address_after}.`;
  }
  /* Whether a call kept talking is read from a counter the data path keeps only
     while it has a bond ring, so with one circuit left it is absent, never zero,
     until the failed circuit is back in the bond. */
  const c = e.calls;
  const one = c?.on_circuit === 1;
  const calls = c && c.on_circuit > 0 && (c.carried_on === undefined
    ? `Of this appliance's own live calls, ${c.on_circuit} ${one ? "was" : "were"} on it. Whether ${one ? "it" : "they"} kept talking is read once the circuit is back in the bond.`
    : `Of this appliance's own live calls, ${c.on_circuit} ${one ? "was" : "were"} on it and ${c.carried_on === c.on_circuit ? (one ? "was" : "all were") : `${c.carried_on} ${c.carried_on === 1 ? "was" : "were"}`} still talking afterwards.`);
  return { what, call, sessions, address, calls };
}

/* Where things are right now, while a watch runs. */
function WatchNow({ now, wanName }) {
  const calls = Object.entries(now.calls || {}).map(([c, n]) => `${n} on ${wanName(c)}`);
  if (now.calls_striped) calls.push(`${now.calls_striped} spread over every circuit`);
  const perCircuit = (now.sessions || []).reduce((acc, x) => ({ ...acc, [x.circuit]: (acc[x.circuit] || 0) + 1 }), {});
  const sessions = Object.entries(perCircuit).map(([c, n]) => `${n} on ${c ? wanName(c) : "no circuit"}`);
  const lines = [
    now.call_striped ? "The test call is spread over every circuit, as a long call is."
      : now.call ? `The test call is on ${wanName(now.call)}.` : null,
    sessions.length > 0 && `Order sessions: ${sessions.join(", ")}.`,
    `This appliance's own live calls: ${calls.length ? calls.join(", ") : "none seen"}.`,
  ].filter(Boolean);
  return (
    <div style={{ marginTop: 12, fontSize: "0.82rem", color: "#0f172a", lineHeight: 1.5 }}>
      <div><b>Watching for {fmtLength(now.watched_sec || 0)}.</b> {lines.join(" ")}</div>
      {now.dark?.length > 0 && (
        <div style={{ color: "#92400e", fontWeight: 600, marginTop: 4 }}>
          Dark right now: {now.dark.map(wanName).join(", ")}.
        </div>
      )}
    </div>
  );
}

function WatchResult({ report, wanName }) {
  const evs = report.events || [];
  const held = !!report.session_peer;
  const sessions = report.sessions || [];
  const live = !!report.now;
  const measuring = evs.some((e) => e.open);
  const zone = zoneOf(evs[0]?.at || report.started_at);
  return (
    <div>
      <div className="vfy-kpis">
        <div className="stat-card">
          <div className="stat-label">Failures seen</div>
          <div className="stat-value">{evs.length}</div>
          <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>
            {measuring ? "measuring the latest" : `${report.events_scored || 0} under the call`}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Longest break</div>
          <div className="stat-value">{report.events_scored ? `${report.worst_gap_sec} s` : measuring ? "measuring" : "none"}</div>
          <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>graded against {report.bound_sec} s</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Sessions kept</div>
          <div className="stat-value">
            {!held ? "not checked" : live ? `${sessions.length} held` : `${sessions.filter((s) => s.kept).length} of ${sessions.length}`}
          </div>
        </div>
        {!live && (
          <div className="stat-card">
            <div className="stat-label">Watched</div>
            <div className="stat-value">{fmtLength(report.watched_sec || 0)}</div>
          </div>
        )}
      </div>

      {live && <WatchNow now={report.now} wanName={wanName} />}

      {evs.length === 0 ? (
        <div className="vfy-samples" style={{ marginTop: 10 }}>
          {live
            ? "Nothing has failed yet. Fail a circuit from outside, with a fault box or by pulling a cable, and it shows here within seconds."
            : "No circuit failed while this run watched."}
        </div>
      ) : (
        <ol style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 }} aria-label="Failures seen">
          {evs.map((e) => {
            const f = failureWords(e, wanName);
            const ok = !e.open && e.on_target && e.gap_sec <= report.bound_sec;
            const over = !e.open && e.on_target && e.gap_sec > report.bound_sec;
            return (
              <li key={e.n} style={{
                borderLeft: `3px solid ${e.open ? "#f59e0b" : over ? "#dc2626" : ok ? "#16a34a" : "#cbd5e1"}`,
                padding: "2px 0 2px 10px", fontSize: "0.82rem", color: "#0f172a", lineHeight: 1.5,
              }}>
                <div style={{ fontWeight: 700 }}>
                  <span style={{ fontVariantNumeric: "tabular-nums", marginRight: 8 }}>{fmtClockSec(e.at)}</span>
                  {f.what}
                </div>
                <div>{f.call}{ok && " Within the bound."}{over && ` Over the ${report.bound_sec} s bound.`}</div>
                {[f.sessions, f.address, f.calls].filter(Boolean).map((line) => <div key={line}>{line}</div>)}
                {e.note && <div style={muted}>{e.note}</div>}
              </li>
            );
          })}
        </ol>
      )}
      {zone && evs.length > 0 && <div style={{ ...muted, marginTop: 6 }}>Times are the appliance's clock, {zone}.</div>}

      {report.breaks_total > 0 && (
        <div className="vfy-samples" style={{ marginTop: 10 }}>
          The call also went quiet {report.breaks_total === 1 ? "once" : `${report.breaks_total} times`} with no failure
          recorded, for up to {report.breaks[0].gap_sec} s ({report.breaks.slice(0, 5).map((b) => fmtClockSec(b.at)).join(", ")}).
          Listed, never graded: the soak suite is the one that explains those.
        </div>
      )}

      {evs.some((e) => !e.open) && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, color: "#475569" }}>Measurements</summary>
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 560 }}>
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left" }}>
                  <th style={th}>Failure</th><th style={th}>Circuit</th><th style={th}>Recorded as</th>
                  <th style={th}>Under the call</th><th style={th}>Break</th><th style={th}>Frames lost</th><th style={th}>Loss</th>
                  {held && <th style={th}>Sessions</th>}
                </tr>
              </thead>
              <tbody>
                {evs.filter((e) => !e.open).map((e) => (
                  <tr key={e.n} style={{ borderTop: "1px solid #f1f5f9", opacity: e.on_target ? 1 : 0.55 }}>
                    <td style={td}>{e.n}</td>
                    <td style={td}>{wanName(e.wan)}</td>
                    <td style={td}>{e.kind === "link" ? "link lost" : "silent, link up"}{e.cause === "impairment" ? ", impaired here" : ""}</td>
                    <td style={td}>{e.on_target ? (e.striped ? "yes, striped" : "yes") : "no, not scored"}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{e.on_target ? `${e.gap_sec} s` : "—"}</td>
                    <td style={td}>{e.gap_frames}</td>
                    <td style={td}>{fmtPct(e.loss_pct)}</td>
                    {held && <td style={td}>{sessionsAcross(e) || "—"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {held && !live && <div className="vfy-samples">{farEnd(report, wanName)}</div>}
        </details>
      )}

      {!live && (
        <div className="vfy-samples">
          {report.continuity_checked
            ? report.continuity_reason
            : "No far end was configured to watch the public address, so this run reports the breaks and makes no claim about the session surviving."}
        </div>
      )}
    </div>
  );
}

/* How each five-minute window of a soak went. Each has an icon and a word, and
   the two ungraded kinds a pattern or an outline besides, so none of them rests
   on hue: excluded is hatched, partial is outlined. */
const WINDOW = {
  pass: { word: "pass", Icon: FiCheckCircle },
  attributed: { word: "attributed", Icon: FiInfo },
  fail: { word: "fail", Icon: FiXCircle },
  excluded: { word: "excluded", Icon: FiSlash },
  partial: { word: "partial", Icon: FiMinusCircle },
};

const BREACH = {
  documented: { word: "documented recovery", Icon: FiInfo },
  below_detection: { word: "below detection", Icon: FiInfo },
  failing: { word: "failing", Icon: FiXCircle },
};

const EXCLUDED = {
  settling: "settling after a previous run",
  impairment: "an impairment",
  policy: "class settings changed",
  instrument: "instrument fault",
};

/* What the call heard across one cut the run made, and how that was graded. */
const cutHeard = (c) => (c.gap_ms > 0 ? `silent ${fmtSpan(c.gap_ms)}` : c.loss_pct > 0 ? `lost ${fmtPct(c.loss_pct)} of a slice` : "no break");
const cutOutcome = (c) => {
  const heard = cutHeard(c);
  const graded = c.class ? `, ${BREACH[c.class]?.word || c.class}` : "";
  const back = c.settle_ms > 0 ? `, circuits back ${fmtSpan(c.settle_ms)} after the cut lifted` : "";
  return `${heard}${graded}${back}`;
};

const fmtLength = (sec) => {
  const s = Math.round(sec || 0);
  if (s < 90) return `${s} s`;
  if (s < 90 * 60) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round((s / 3600) * 10) / 10} h`;
  return fmtDuration(s);
};
const fmtSpan = (ms) => (ms < 1000 ? `${Math.round(ms)} ms` : `${Math.round(ms / 100) / 10} s`);
/* A soak's times are the appliance's clock, read from each time as the report
   wrote it, on a 24 hour dial, so a table and the run's own sentences agree
   wherever the report is opened. A browser's Date would move them into the
   reader's zone. */
const WALL = /T(\d\d):(\d\d)/;
const fmtClock = (iso) => {
  const m = WALL.exec(String(iso));
  return m ? `${m[1]}:${m[2]}` : String(iso);
};
/* To the second, for a failure: two a minute apart in a demo are told apart. */
const fmtClockSec = (iso) => {
  const m = /T(\d\d:\d\d:\d\d)/.exec(String(iso));
  return m ? m[1] : String(iso || "");
};
const fmtMinute = (iso) => {
  const m = WALL.exec(String(iso));
  return m ? `:${m[2]}` : "";
};
const zoneOf = (iso) => {
  const m = /(Z|[+-]\d\d:\d\d)$/.exec(String(iso));
  if (!m) return "";
  return m[1] === "Z" ? "UTC" : `UTC${m[1]}`;
};

function breachWhat(b) {
  switch (b.kind) {
    case "silence": return `silence ${fmtSpan(b.span_ms)}`;
    case "loss": return `loss ${fmtPct(b.value)} over ${fmtSpan(b.span_ms)}`;
    default: return `delay variation ${Math.round(b.value)} ms`;
  }
}

function breachCircuits(b, wanName) {
  const names = (b.circuits || []).map(wanName).join(" and ") || "not placed";
  return b.striped ? `striped across ${names}` : names;
}

/* Where the call spent a stretch of windows, by display name, largest first. */
function laneOf(windows, wanName) {
  const onCircuit = {};
  let striped = 0;
  for (const w of windows) {
    striped += w.call?.striped_sec || 0;
    for (const [c, sec] of Object.entries(w.call?.circuit_sec || {})) onCircuit[c] = (onCircuit[c] || 0) + sec;
  }
  const parts = Object.entries(onCircuit).sort((a, b) => b[1] - a[1])
    .map(([c, sec]) => `${wanName(c)} ${fmtLength(sec)}`);
  if (striped > 0) parts.push(`striped ${fmtLength(striped)}`);
  return parts.length ? `Call on ${parts.join(", ")}` : "Call placement not recorded";
}

/* A trading day soak: hours of five-minute windows, each one a button that
   opens its detail, with every break the run graded listed beneath. The
   breaches table is always open, so a screenshot of the result carries it. */
function SoakResult({ report, wanName }) {
  const [picked, setPicked] = useState(null);
  const windows = report.windows || [];
  const breaches = report.breaches || [];
  const cuts = report.cuts || [];
  const worstCut = Math.max(0, ...cuts.map((c) => c.gap_ms || 0));
  const counts = report.breach_counts || {};
  const explained = (counts.documented || 0) + (counts.below_detection || 0);
  const total = explained + (counts.failing || 0);
  const zone = zoneOf(report.started_at);
  const hours = [];
  windows.forEach((w, i) => {
    if (i % 12 === 0) hours.push([]);
    hours[hours.length - 1].push({ w, i });
  });
  const chosen = picked !== null ? windows[picked] : null;

  return (
    <div>
      <div className="vfy-kpis">
        <div className="stat-card">
          <div className="stat-label">Failing breaks</div>
          <div className="stat-value">{counts.failing || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Explained breaks</div>
          <div className="stat-value">{explained}</div>
          <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>
            {counts.documented || 0} documented, {counts.below_detection || 0} below detection
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Worst silence</div>
          <div className="stat-value">{report.worst_gap_ms > 0 ? fmtSpan(report.worst_gap_ms) : "none"}</div>
          <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>in the time graded</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Measured</div>
          <div className="stat-value">{fmtLength(report.measured_sec)}</div>
          <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>{fmtLength(report.excluded_sec)} excluded</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Striped</div>
          <div className="stat-value">{fmtLength(report.striped_sec)}</div>
          <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>gate open {fmtLength(report.gate_open_sec)}</div>
        </div>
        {cuts.length > 0 && (
          <div className="stat-card">
            <div className="stat-label">Cuts made</div>
            <div className="stat-value">{cuts.length}</div>
            <div style={{ ...muted, fontSize: "0.68rem", marginTop: 3 }}>
              every {report.params?.cut_every_min} min, longest silence {worstCut > 0 ? fmtSpan(worstCut) : "none"}
              {cuts.some((c) => c.striped) && `, ${cuts.filter((c) => c.striped).length} under a striped call`}
            </div>
          </div>
        )}
      </div>

      {zone && <div style={{ ...muted, marginTop: 10 }}>Times are the appliance's clock, {zone}.</div>}
      {hours.map((row) => (
        <div className="vfy-soak-hour" key={row[0].w.start}>
          <div className="vfy-soak-hour-label">{fmtClock(row[0].w.start)}</div>
          <div>
            <div className="vfy-soak-cells" role="group" aria-label={`Windows from ${fmtClock(row[0].w.start)}`}>
              {row.map(({ w, i }) => {
                const s = WINDOW[w.status] || { word: w.status, Icon: FiAlertCircle };
                return (
                  <button key={w.start} type="button" className={`vfy-soak-cell is-${w.status}`}
                    aria-pressed={picked === i} aria-label={`${fmtClock(w.start)}, ${s.word}`}
                    onClick={() => setPicked(picked === i ? null : i)}>
                    <span className="vfy-soak-cell-time">{fmtMinute(w.start)}</span>
                    <span className="vfy-soak-cell-status"><s.Icon size={11} aria-hidden="true" />{s.word}</span>
                  </button>
                );
              })}
            </div>
            <div className="vfy-soak-lane">{laneOf(row.map((x) => x.w), wanName)}</div>
          </div>
        </div>
      ))}

      {chosen && (
        <div className="vfy-soak-detail">
          <div className="vfy-words-title">
            {fmtClock(chosen.start)}, {fmtLength(chosen.measured_sec)}: {(WINDOW[chosen.status] || {}).word || chosen.status}
          </div>
          <div className="vfy-samples">
            {chosen.sent} frames sent, {chosen.lost} lost ({fmtPct(chosen.loss_pct)}), longest silence
            {" "}{chosen.worst_gap_ms > 0 ? fmtSpan(chosen.worst_gap_ms) : "none"}, delay variation p95 {chosen.jitter_p95_ms} ms.
          </div>
          {Object.keys(chosen.excluded || {}).length > 0 && (
            <div className="vfy-samples">
              Not graded: {Object.entries(chosen.excluded).map(([why, sec]) => `${EXCLUDED[why] || why} ${fmtLength(sec)}`).join(", ")}.
            </div>
          )}
          {Object.keys(chosen.changes || {}).length > 0 && (
            <div className="vfy-samples">
              Changes: {Object.entries(chosen.changes).map(([kind, t]) => `${kind.replace(/_/g, " ")} ${t.count}`).join(", ")}.
            </div>
          )}
          {chosen.instrument && (
            <div className="vfy-samples">
              Instrument: sender at most {chosen.instrument.sender_late_max_ms} ms late, {chosen.instrument.late} late
              {" "}replies, {chosen.instrument.reordered} reordered, {chosen.instrument.reader_drops} reader drops,
              {" "}{chosen.instrument.send_errors} send errors
              {chosen.instrument.send_error ? `, the first refused with "${chosen.instrument.send_error}"` : ""}.
            </div>
          )}
        </div>
      )}

      {cuts.length > 0 && (
        <>
          <div className="vfy-words-title" style={{ marginTop: 14 }}>Cuts this run made</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 480 }}>
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left" }}>
                  <th style={th}>#</th><th style={th}>{zone ? `When (${zone})` : "When"}</th><th style={th}>Circuit cut</th>
                  <th style={th}>Call was</th><th style={th}>Heard</th><th style={th}>Graded</th><th style={th}>Circuits back</th>
                </tr>
              </thead>
              <tbody>
                {cuts.map((c) => {
                  const cls = c.class ? (BREACH[c.class] || { word: c.class, Icon: FiAlertCircle }) : null;
                  return (
                    <tr key={c.n} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <td style={td}>{c.n}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtClock(c.at)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{wanName(c.circuit)}</td>
                      <td style={td}>{c.striped ? "striped across the bond" : "on that circuit"}</td>
                      <td style={td}>{cutHeard(c)}</td>
                      <td style={td}>
                        {cls ? <span className={`vfy-soak-class is-${c.class}`}><cls.Icon size={11} aria-hidden="true" /> {cls.word}</span> : "—"}
                      </td>
                      <td style={td}>{c.settle_ms > 0 ? `${fmtSpan(c.settle_ms)} after the cut lifted` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="vfy-words-title" style={{ marginTop: 14 }}>Breaks</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 560 }}>
          <thead>
            <tr style={{ color: "#64748b", textAlign: "left" }}>
              <th style={th}>{zone ? `When (${zone})` : "When"}</th><th style={th}>Break</th><th style={th}>Class</th>
              <th style={th}>Circuits</th><th style={th}>What was recorded</th>
            </tr>
          </thead>
          <tbody>
            {breaches.length === 0 && (
              <tr style={{ borderTop: "1px solid #f1f5f9" }}><td style={td} colSpan={5}>No breaks.</td></tr>
            )}
            {breaches.map((b) => {
              const c = BREACH[b.class] || { word: b.class, Icon: FiAlertCircle };
              return (
                <tr key={`${b.at}-${b.kind}`} style={{ borderTop: "1px solid #f1f5f9", verticalAlign: "top" }}>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtClock(b.at)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{breachWhat(b)}</td>
                  <td style={td}>
                    <span className={`vfy-soak-class is-${b.class}`}><c.Icon size={11} aria-hidden="true" /> {c.word}</span>
                  </td>
                  <td style={td}>{breachCircuits(b, wanName)}</td>
                  <td style={{ ...td, color: "#334155" }}>{b.explanation}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {breaches.length < total && (
        <div className="vfy-samples">The first {breaches.length} of {total} breaks are listed; every one is counted.</div>
      )}
      {report.direct_lines?.reason && (
        <div className="vfy-samples" style={{ color: "#92400e" }}>
          {report.direct_lines.enabled ? "Direct lines: " : "No break could be below detection: "}{report.direct_lines.reason}
        </div>
      )}

      <SoakCircuits windows={chosen ? [chosen] : windows} scope={chosen ? fmtClock(chosen.start) : "whole run"} wanName={wanName} />
    </div>
  );
}

/* Circuit diversity: what each pair of circuits was found to share.
 *
 * One card per pair, its three rows nearest the appliance first. A row's
 * finding is a word as well as a tint, and the anchor every circuit shares is
 * stated before any of it, because it bounds everything the rows can say. */
const DIVERSITY_ROWS = [["on_premises", "On premises"], ["capacity", "Capacity"], ["route", "Route"]];

/* None found is neutral, not green: the suite never passes, and nothing found
   is no evidence that nothing is shared. A discarded run's rows keep their words
   but lose their tint, because the rule graded none of them. */
const FINDING_CHIP = { shared: "is-high", "not measured": "is-moderate" };

function FindingChip({ finding, graded = true }) {
  const f = finding || "not measured";
  return <span className={`vfy-chip ${(graded && FINDING_CHIP[f]) || ""}`}>{f}</span>;
}

const fmtIndex = (v) => (typeof v === "number" ? v.toFixed(2) : "none");
const median = (xs) => {
  const s = [...xs].sort((x, y) => x - y);
  return s.length ? (s[(s.length - 1) >> 1] + s[s.length >> 1]) / 2 : 0;
};

function DiversityResult({ report, wanName }) {
  const circuits = Object.fromEntries((report.circuits || []).map((c) => [c.name, c]));
  const anchor = report.anchor || {};
  const discarded = report.carrier_changed?.length > 0;
  return (
    <div>
      <div className="vfy-samples">
        Up to the cloud anchor{anchor.host ? ` at ${anchor.host}` : ""}, which every circuit here shares and where
        independence stops.
        {anchor.host && !anchor.load_allowed && " This anchor is not listed to take a run's load, so capacity was not measured."}
      </div>
      {(report.pairs || []).map((p) => (
        <div className="vfy-dir" key={`${p.a}+${p.b}`}>
          <div className="vfy-dir-head">
            <span>{wanName(p.a)} and {wanName(p.b)}</span>
            {discarded ? <FindingChip finding="discarded" /> : <FindingChip finding={p.finding} />}
            {p.nearest && <span style={{ textTransform: "none", letterSpacing: 0 }}>nearest: {p.nearest}</span>}
          </div>
          {discarded && (
            <div className="vfy-samples">A link event discarded this run, so these rows are what was measured and none is graded.</div>
          )}
          {DIVERSITY_ROWS.map(([key, title]) => (
            <div key={key} style={{ borderTop: "1px solid #f1f5f9", padding: "8px 0 4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#0f172a" }}>{title}</span>
                <FindingChip finding={p[key]?.finding} graded={!discarded} />
              </div>
              {p[key]?.reason && <div className="vfy-samples">{p[key].reason}</div>}
              {key === "on_premises" && <GatewaysDetail a={circuits[p.a]} b={circuits[p.b]} wanName={wanName} />}
              {key === "capacity" && (p.capacity?.directions || []).map((d) => (
                <CapacityDetail key={d.direction} d={d} a={circuits[p.a] || { name: p.a }} b={circuits[p.b] || { name: p.b }}
                  wanName={wanName} />
              ))}
              {key === "route" && <RouteDetail route={p.route || {}} a={p.a} b={p.b} traces={report.traces} wanName={wanName} />}
            </div>
          ))}
        </div>
      ))}
      {report.carrier_changed?.length > 0 && (
        <div className="vfy-drift">
          Link event on {report.carrier_changed.map(wanName).join(", ")} during the run; the run is discarded.
        </div>
      )}
    </div>
  );
}

/* Each circuit on its own, measured straight to the anchor and never graded:
   the run grades only the call. Across the whole run the direct figures are the
   worst window's and the times are totals. */
function SoakCircuits({ windows, scope, wanName }) {
  const rows = {};
  for (const w of windows) {
    for (const [c, v] of Object.entries(w.circuits || {})) {
      const r = rows[c] || { loss: 0, gap: 0, degraded: 0, silent: 0, out: 0, exempt: 0 };
      rows[c] = {
        loss: Math.max(r.loss, v.direct_loss_pct || 0), gap: Math.max(r.gap, v.direct_gap_ms || 0),
        degraded: r.degraded + (v.degraded_sec || 0), silent: r.silent + (v.silent_sec || 0),
        out: r.out + (v.out_of_ring_sec || 0), exempt: r.exempt + (v.exempt_sec || 0),
      };
    }
  }
  const names = Object.keys(rows).sort();
  if (names.length === 0) return null;
  return (
    <>
      <div className="vfy-words-title" style={{ marginTop: 14 }}>Circuits: bare circuit, not graded ({scope})</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 560 }}>
          <thead>
            <tr style={{ color: "#64748b", textAlign: "left" }}>
              <th style={th}>Circuit</th><th style={th}>Direct loss</th><th style={th}>Longest direct gap</th>
              <th style={th}>Degraded</th><th style={th}>Silent</th><th style={th}>Out of ring</th><th style={th}>Exempt</th>
            </tr>
          </thead>
          <tbody>
            {names.map((c) => (
              <tr key={c} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={{ ...td, fontWeight: 700 }}>{wanName(c)}</td>
                <td style={td}>{fmtPct(rows[c].loss)}</td>
                <td style={td}>{rows[c].gap > 0 ? fmtSpan(rows[c].gap) : "none"}</td>
                <td style={td}>{fmtLength(rows[c].degraded)}</td>
                <td style={td}>{fmtLength(rows[c].silent)}</td>
                <td style={td}>{fmtLength(rows[c].out)}</td>
                <td style={td}>{fmtLength(rows[c].exempt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function GatewaysDetail({ a, b, wanName }) {
  const line = (c) => c && `${wanName(c.name)}: gateway ${c.gateway || "unknown"} answers from ${c.gateway_mac || "no hardware address"}`
    + (c.mac_kind && c.mac_kind !== "physical" ? ` (${c.mac_kind.toUpperCase()}, a virtual router address)` : "");
  return <div className="vfy-samples">{[line(a), line(b)].filter(Boolean).join(". ")}.</div>;
}

/* One direction: an index strip marked where the rule reads it, the circuits
   alone against the pair together, and what the other circuit's probes saw. */
function CapacityDetail({ d, a, b, wanName }) {
  const blocks = d.blocks || [];
  const usable = blocks.filter((x) => !x.void_reason && typeof x.index === "number");
  const lo = -0.2;
  const hi = 1.2;
  const at = (v) => `${(100 * (Math.min(hi, Math.max(lo, v)) - lo)) / (hi - lo)}%`;
  const aloneOf = (name) => median(usable.map((x) => x.alone_mbps?.[name] || 0));
  const rows = [
    { key: a.name, label: `${wanName(a.name)} alone`, mbps: aloneOf(a.name), prior: d.direction === "upload" ? a.prior_up_mbps : a.prior_down_mbps },
    { key: b.name, label: `${wanName(b.name)} alone`, mbps: aloneOf(b.name), prior: d.direction === "upload" ? b.prior_up_mbps : b.prior_down_mbps },
    { key: "together", label: "Together", mbps: median(usable.map((x) => x.together_mbps || 0)), together: true },
  ];
  const peak = Math.max(1, ...rows.map((r) => r.mbps));
  return (
    <div style={{ marginTop: 8 }}>
      <div className="vfy-samples" style={{ fontWeight: 600, color: "#334155" }}>
        {d.direction}: {d.finding}{d.localised ? `, queue placed ${d.localised}` : ""}
      </div>
      <div role="img" aria-label={`Index per block: ${blocks.map((x) => (x.void_reason ? "void" : fmtIndex(x.index))).join(", ")}; marked at 0.3 and 0.7`}
        style={{ position: "relative", height: 22, margin: "6px 0", background: "#eef2f7", borderRadius: 4 }}>
        {[0.3, 0.7].map((m) => (
          <span key={m} style={{ position: "absolute", left: at(m), top: 0, bottom: 0, borderLeft: "1px dashed #64748b" }} />
        ))}
        {blocks.filter((x) => typeof x.index === "number").map((x) => (
          <span key={x.n} title={`block ${x.n}: ${fmtIndex(x.index)}${x.void_reason ? ", void" : ""}`}
            style={{
              position: "absolute", left: at(x.index), top: 6, width: 10, height: 10, marginLeft: -5, borderRadius: "50%",
              background: x.void_reason ? "#fff" : "var(--accent)", border: "2px solid var(--accent)", boxSizing: "border-box",
            }} />
        ))}
      </div>
      <div className="vfy-samples" style={{ marginTop: 0 }}>
        Index per block, marked at 0.3 and 0.7: {blocks.map((x) => `block ${x.n} ${x.void_reason ? "void" : fmtIndex(x.index)}`).join(", ")}.
      </div>
      {usable.length > 0 && rows.map((r) => (
        <div className="vfy-bar-row" key={r.key}>
          <span className="vfy-bar-name">{r.label}{r.prior ? <span className="vfy-bar-best"> prior {fmtMbps(r.prior)}</span> : null}</span>
          <span className="vfy-bar-track">
            <span className={`vfy-bar-fill${r.together ? "" : " vfy-bar-fill--alone"}`} style={{ width: `${Math.max(2, (100 * r.mbps) / peak)}%` }} />
          </span>
          <span className="vfy-bar-value">{fmtMbps(r.mbps)}</span>
        </div>
      ))}
      <CrossLoadTable blocks={blocks} wanName={wanName} />
      {blocks.filter((x) => x.void_reason).map((x) => (
        <div className="vfy-samples" key={x.n}>Block {x.n} left out: {x.void_reason}.</div>
      ))}
    </div>
  );
}

/* Every probe reading, folded across blocks: how often each target rose while
   the other circuit was loaded, by how much, and how often the gate set it
   aside. A delay is never printed with a minus sign. */
function CrossLoadTable({ blocks, wanName }) {
  const byKey = new Map();
  for (const x of blocks) {
    for (const c of x.cross_load || []) {
      const key = `${c.loaded}|${c.observed || ""}|${c.target}`;
      const agg = byKey.get(key) || { ...c, n: 0, rose: 0, voids: 0, added: [], gate: 0 };
      agg.n += 1;
      if (c.void) agg.voids += 1;
      else if (c.rose) agg.rose += 1;
      if (!c.void && c.added_ms > 0) agg.added.push(c.added_ms);
      agg.gate = Math.max(agg.gate, c.softirq_pct || 0);
      byKey.set(key, agg);
    }
  }
  const rows = [...byKey.values()];
  if (rows.length === 0) return null;
  const timed = (r) => (r.target === "loopback" ? "this appliance"
    : `${wanName(r.observed)} ${r.target}${r.observed === r.loaded ? " (its own)" : ""}`);
  return (
    <div style={{ overflowX: "auto", marginTop: 6 }}>
      <table style={{ borderCollapse: "collapse", fontSize: "0.74rem", minWidth: 520 }}>
        <thead>
          <tr style={{ color: "#64748b", textAlign: "left" }}>
            <th style={th}>Loaded</th><th style={th}>Timed</th><th style={th}>Rose</th>
            <th style={th}>Added</th><th style={th}>Interrupt gate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.loaded}|${r.observed}|${r.target}`} style={{ borderTop: "1px solid #f1f5f9" }}>
              <td style={td}>{wanName(r.loaded)}</td>
              <td style={td}>{timed(r)}<div style={{ ...muted, fontSize: "0.66rem" }}>{r.address}</div></td>
              <td style={td}>{r.rose} of {r.n - r.voids} blocks{r.voids ? `, ${r.voids} set aside` : ""}</td>
              <td style={td}>{r.added.length ? `up to ${Math.max(...r.added)} ms` : "none measurable"}</td>
              <td style={td}>{r.target === "loopback" ? "not gated" : `busiest core ${Math.round(r.gate)}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* The two routes side by side, TTL for TTL, from the first sweep each way that
   the run kept; a router both share is bold. An older run keeps no sweeps, and
   is left with the routers and facts the rule reported. */
function RouteDetail({ route, a, b, traces, wanName }) {
  const shared = new Set((route.shared_routers || []).map((s) => s.ip));
  const facts = [
    route.common_first_as && `both start in AS${route.common_first_as.asn}${route.common_first_as.name ? ` (${route.common_first_as.name})` : ""}`,
    route.same_public_address && `both arrive from ${route.same_public_address}`,
    route.adjacent?.length && `adjacent addresses ${route.adjacent.join("; ")}`,
    route.meet_later?.length && `meet later at ${route.meet_later.join(", ")}`,
    typeof route.silent_frac === "number" && `${Math.round(route.silent_frac * 100)}% of hops before the anchor silent`,
  ].filter(Boolean);
  const hopsOf = (name) => (traces?.forward?.[name]?.[0]?.hops || []);
  const ha = hopsOf(a);
  const hb = hopsOf(b);
  const depth = Math.max(ha.length, hb.length);
  const cell = (h) => {
    if (!h || !h.ip) return <span style={muted}>no reply</span>;
    const addrs = [h.ip, h.alt_ip].filter(Boolean);
    return (
      <span>
        {addrs.map((ip, i) => (
          <span key={ip} style={shared.has(ip) ? { fontWeight: 700 } : undefined}>{i ? " / " : ""}{ip}</span>
        ))}
        {h.asn ? <span style={muted}> AS{h.asn}</span> : null}
      </span>
    );
  };
  return (
    <div>
      {(route.shared_routers || []).map((s) => (
        <div className="vfy-samples" key={`${s.ip}|${s.direction}`}>
          <b>{s.ip}</b>{s.asn ? ` (AS${s.asn})` : ""} on both {s.direction} routes, hop {s.ttl_a} and hop {s.ttl_b}, in {s.sweeps?.[0]} and {s.sweeps?.[1]} sweeps.
        </div>
      ))}
      {facts.length > 0 && <div className="vfy-samples">Reported, not graded: {facts.join("; ")}.</div>}
      {depth > 0 && (
        <div style={{ overflowX: "auto", marginTop: 6 }}>
          <table className="cards-on-phone" style={{ borderCollapse: "collapse", fontSize: "0.74rem", minWidth: 420 }}>
            <thead>
              <tr style={{ color: "#64748b", textAlign: "left" }}>
                <th style={th}>Hop</th><th style={th}>{wanName(a)}</th><th style={th}>{wanName(b)}</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: depth }, (_, i) => (
                <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={td} data-label="Hop">{i + 1}</td><td style={td} data-label={wanName(a)}>{cell(ha[i])}</td><td style={td} data-label={wanName(b)}>{cell(hb[i])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const fmtSec = (ms, digits = 1) => `${(ms / 1000).toFixed(digits)} s`;

/* What the held sessions on the cut circuit did across one failure: whether
   one was there, whether it resumed, and the longest wait with the resend that
   got through. Null on an event from a run that held none. */
function sessionsAcross(e) {
  if (!e.sessions) return null;
  const on = e.sessions.filter((s) => s.on_target);
  if (on.length === 0) return "none on this circuit";
  const waited = (s) => Math.max(s.wait_up_ms || 0, s.wait_down_ms || 0);
  const longest = on.reduce((a, s) => (waited(s) > waited(a) ? s : a));
  const stuck = on.filter((s) => !s.resumed_up).length;
  if (stuck > 0) {
    const who = stuck === on.length ? "did not resume" : `${stuck} of ${on.length} did not resume`;
    return `${who}, silent ${fmtSec(waited(longest))} or more`;
  }
  const landed = longest.landed_resend > 0 && longest.resends_ms?.[longest.landed_resend - 1];
  return `resumed after up to ${fmtSec(waited(longest))}` + (landed ? `, through on the ${fmtSec(landed, 2)} resend` : "");
}

/* The far end the sessions were held to, and where it saw them come from. */
function farEnd(report, wanName) {
  const sp = report.session_peer;
  const uncovered = sp.uncovered || [];
  return `Far end ${sp.address}` + (sp.observed_address ? `, which saw this appliance as ${sp.observed_address}` : "")
    + (sp.colocated_with_anchor ? ", on the anchor's own host" : "") + "."
    + (uncovered.length ? ` No session landed on ${uncovered.map(wanName).join(", ")}, so cuts there are not scored for sessions.` : "");
}

/* Loss to two places at most. A probe's third decimal is below its own
   resolution, and printing it beside a rounded neighbour reads as a precision
   one figure has and the other does not. */
function fmtPct(v) {
  return typeof v === "number" ? `${Math.round(v * 100) / 100}%` : "—";
}

/* A difference smaller than the run's own spread is not a measurement of
   anything, and a negative one is noise with a sign on it. */
function fmtAdded(ms, spread) {
  if (typeof ms !== "number") return "—";
  if (ms <= (spread || 0)) return "none measurable";
  return `${ms} ms`;
}

const RESULTS = {
  bond: BondResult, lane: LaneResult, voice: VoiceResult, tunnel: TunnelResult, failover: FailoverResult,
  watch: WatchResult, soak: SoakResult, diversity: DiversityResult,
};

export default RESULTS;
